import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { siteConfig } from "@/config/site";
import { supabase } from "@/lib/supabase";
import { Save, Check, Globe, Truck, Info, Mail, Receipt, Percent } from "lucide-react";
import {
  getStoredShippingSettings,
  saveShippingSettingsToSupabase,
  getStoredTaxSettings,
  saveTaxSettingsToSupabase,
  SETTINGS_KEY,
} from "@/lib/store-settings";

export const Route = createFileRoute("/admin/settings")({
  head: () => ({
    meta: [{ title: `Store Settings — Admin — ${siteConfig.name}` }],
  }),
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const initialShipping = getStoredShippingSettings();
  const initialTax = getStoredTaxSettings();

  const [storeName, setStoreName] = useState<string>(siteConfig.name);
  const [currency, setCurrency] = useState<string>(siteConfig.currency);
  const [supportEmail, setSupportEmail] = useState<string>(siteConfig.contact.email);

  // Fulfillment & Shipping state
  const [standardShippingFee, setStandardShippingFee] = useState<number | "">(
    initialShipping.standardShippingFee,
  );
  const [expressShippingFee, setExpressShippingFee] = useState<number | "">(
    initialShipping.expressShippingFee,
  );
  const [freeShippingThreshold, setFreeShippingThreshold] = useState<number | "">(
    initialShipping.freeShippingThreshold,
  );
  const [standardDays, setStandardDays] = useState<string>(initialShipping.standardDays);
  const [expressDays, setExpressDays] = useState<string>(initialShipping.expressDays);

  // Taxation & GST state
  const [pricesIncludeGst, setPricesIncludeGst] = useState<boolean>(initialTax.pricesIncludeGst);
  const [gstRatePercent, setGstRatePercent] = useState<number | "">(
    Math.round(initialTax.gstRate * 100),
  );

  const [saved, setSaved] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    // 1. Load from local cache
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.storeName) setStoreName(parsed.storeName);
        if (parsed.currency) setCurrency(parsed.currency);
        if (parsed.supportEmail) setSupportEmail(parsed.supportEmail);
        if (typeof parsed.freeShippingThreshold === "number")
          setFreeShippingThreshold(parsed.freeShippingThreshold);
        if (typeof parsed.standardShippingFee === "number")
          setStandardShippingFee(parsed.standardShippingFee);
        if (typeof parsed.expressShippingFee === "number")
          setExpressShippingFee(parsed.expressShippingFee);
        if (parsed.standardDays) setStandardDays(parsed.standardDays);
        if (parsed.expressDays) setExpressDays(parsed.expressDays);
      }
    } catch {
      // Ignore local storage errors
    }

    // 2. Fetch from Supabase store_settings table
    supabase
      .from("store_settings")
      .select("*")
      .then(({ data, error }) => {
        if (!error && data) {
          data.forEach((row) => {
            if (row.key === "general" && row.value) {
              if (row.value.storeName) setStoreName(row.value.storeName);
              if (row.value.currency) setCurrency(row.value.currency);
              if (row.value.supportEmail) setSupportEmail(row.value.supportEmail);
            }
            if (row.key === "shipping" && row.value) {
              if (typeof row.value.standardShippingFee === "number")
                setStandardShippingFee(row.value.standardShippingFee);
              if (typeof row.value.expressShippingFee === "number")
                setExpressShippingFee(row.value.expressShippingFee);
              if (typeof row.value.freeShippingThreshold === "number")
                setFreeShippingThreshold(row.value.freeShippingThreshold);
              if (row.value.standardDays || row.value.estimatedDays)
                setStandardDays(row.value.standardDays || row.value.estimatedDays);
              if (row.value.expressDays) setExpressDays(row.value.expressDays);
            }
            if (row.key === "tax" && row.value) {
              if (typeof row.value.pricesIncludeGst === "boolean")
                setPricesIncludeGst(row.value.pricesIncludeGst);
              if (typeof row.value.gstRate === "number")
                setGstRatePercent(Math.round(row.value.gstRate * 100));
            }
          });
        }
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const generalPayload = {
      storeName,
      currency,
      currencySymbol: currency === "INR" ? "₹" : "$",
      supportEmail,
    };

    const shippingPayload = {
      standardShippingFee: Math.max(0, Number(standardShippingFee) || 0),
      expressShippingFee: Math.max(0, Number(expressShippingFee) || 0),
      freeShippingThreshold: Math.max(0, Number(freeShippingThreshold) || 0),
      standardDays: standardDays.trim() || "5–7",
      expressDays: expressDays.trim() || "1–2",
    };

    const taxPayload = {
      pricesIncludeGst: Boolean(pricesIncludeGst),
      gstRate: (Number(gstRatePercent) || 18) / 100,
    };

    try {
      // 1. Authoritatively update Supabase store_settings table
      const { error: genError } = await supabase.from("store_settings").upsert([
        { key: "general", value: generalPayload, updated_at: new Date().toISOString() },
      ]);
      if (genError) throw new Error(genError.message);

      // 2. Save shipping settings using unified store settings service
      await saveShippingSettingsToSupabase(shippingPayload);

      // 3. Save tax settings using unified store settings service
      await saveTaxSettingsToSupabase(taxPayload);

      // 4. Cache combined payload in localStorage
      try {
        localStorage.setItem(
          SETTINGS_KEY,
          JSON.stringify({
            ...generalPayload,
            ...shippingPayload,
            ...taxPayload,
          }),
        );
      } catch {}

      setSaved(true);
      setErrorMsg(null);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      console.error("Supabase store settings save failed:", err);
      const message = (err as { message?: string }).message;
      setErrorMsg(message || "Failed to persist store settings to database.");
      setSaved(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl pb-16">
      {/* Page Header */}
      <div className="border-b border-border pb-6">
        <p className="eyebrow text-xs text-primary font-semibold">System Preferences</p>
        <h1 className="mt-1 font-display text-3xl text-foreground">Store Configuration</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Manage global store metadata, currency, fulfillment rules, and delivery shipping charges.
        </p>
      </div>

      {saved && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 p-4 rounded border border-emerald-200 shadow-xs animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-600 shrink-0" />
          <span>Store settings and shipping rates saved successfully! Configuration updated in database.</span>
        </div>
      )}

      {errorMsg && (
        <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 p-4 rounded border border-rose-200 shadow-xs animate-in fade-in duration-150">
          <span>Failed to save settings: {errorMsg}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Store Details */}
        <div className="bg-card border border-border p-6 rounded-lg shadow-2xs space-y-4">
          <div className="flex items-center gap-2 border-b border-border pb-3">
            <Globe className="h-4 w-4 text-primary" />
            <h3 className="font-display text-lg text-foreground">General Information</h3>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Store Brand Name</label>
              <input
                type="text"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded"
              />
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Primary Currency Symbol</label>
              <input
                type="text"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="eyebrow text-[11px] block mb-1.5">Customer Support Email</label>
              <div className="relative">
                <Mail className="h-3.5 w-3.5 text-muted-foreground absolute left-3 top-3.5" />
                <input
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  className="min-h-10 w-full border border-border bg-background pl-9 pr-3 text-xs outline-none focus:border-primary rounded"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fulfillment & Shipping Charges */}
        <div className="bg-card border border-border p-6 rounded-lg shadow-2xs space-y-4">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-primary" />
              <h3 className="font-display text-lg text-foreground">Shipping Charges & Fulfillment Rules</h3>
            </div>
            <span className="text-[10px] font-mono uppercase bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">
              Live Checkout Sync
            </span>
          </div>

          <p className="text-xs text-muted-foreground">
            Configure delivery fees, free delivery threshold, and estimated transit times. Changes apply immediately to Cart, Checkout, and customer order calculations.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="eyebrow text-[11px] block mb-1.5">
                Standard Shipping Fee (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={standardShippingFee}
                onChange={(e) =>
                  setStandardShippingFee(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="min-h-10 w-full border border-border bg-background px-3 text-xs font-semibold tabular-nums outline-none focus:border-primary rounded"
                placeholder="e.g. 60"
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Base fee charged on standard delivery when order is below free threshold.
              </span>
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">
                Express Shipping Fee (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={expressShippingFee}
                onChange={(e) =>
                  setExpressShippingFee(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="min-h-10 w-full border border-border bg-background px-3 text-xs font-semibold tabular-nums outline-none focus:border-primary rounded"
                placeholder="e.g. 699"
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Flat fee for expedited courier shipping across India.
              </span>
            </div>

            <div className="sm:col-span-2">
              <label className="eyebrow text-[11px] block mb-1.5">
                Free Shipping Order Threshold (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={freeShippingThreshold}
                onChange={(e) =>
                  setFreeShippingThreshold(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="min-h-10 w-full border border-border bg-background px-3 text-xs font-semibold tabular-nums outline-none focus:border-primary rounded"
                placeholder="e.g. 5000"
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Orders with cart subtotal at or above this amount receive free standard shipping. Set to 0 for free shipping on all orders.
              </span>
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">
                Standard Delivery Days SLA
              </label>
              <input
                type="text"
                value={standardDays}
                onChange={(e) => setStandardDays(e.target.value)}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded"
                placeholder="e.g. 5–7"
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Displayed to customers during checkout and on policy pages.
              </span>
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">
                Express Delivery Days SLA
              </label>
              <input
                type="text"
                value={expressDays}
                onChange={(e) => setExpressDays(e.target.value)}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded"
                placeholder="e.g. 1–2"
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Displayed for priority courier options.
              </span>
            </div>
          </div>

          <div className="rounded-md bg-secondary/30 border border-border/80 p-3.5 flex items-start gap-2.5 text-xs text-muted-foreground mt-2">
            <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p>
              Current rule: Standard shipping is <strong>₹{standardShippingFee === "" ? 0 : standardShippingFee}</strong> (Free on orders ≥ <strong>₹{freeShippingThreshold === "" ? 0 : freeShippingThreshold}</strong>). Express shipping is <strong>₹{expressShippingFee === "" ? 0 : expressShippingFee}</strong>.
            </p>
          </div>
        </div>

        {/* 3. Taxation & GST Configuration */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-xs space-y-6">
          <div className="flex items-center gap-3 border-b border-border pb-4">
            <div className="grid h-9 w-9 place-items-center rounded bg-primary/10 text-primary">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-base font-bold text-foreground">
                Taxation &amp; GST Configuration
              </h2>
              <p className="text-xs text-muted-foreground">
                Configure how Goods &amp; Services Tax (GST) is calculated across product pricing and checkout.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="eyebrow text-[11px] block mb-2">
                GST Pricing Mode
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                    pricesIncludeGst
                      ? "border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20"
                      : "border-border hover:bg-secondary/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="gstMode"
                    checked={pricesIncludeGst}
                    onChange={() => setPricesIncludeGst(true)}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-foreground block">
                      GST Inclusive (MRP Standard - Recommended)
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-relaxed block mt-1">
                      Product selling price already includes GST. No extra tax is added on top at checkout (e.g. ₹200 item + ₹60 shipping = <strong>₹260</strong> total).
                    </span>
                  </div>
                </label>

                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                    !pricesIncludeGst
                      ? "border-amber-600 bg-amber-50/50 dark:bg-amber-950/20"
                      : "border-border hover:bg-secondary/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="gstMode"
                    checked={!pricesIncludeGst}
                    onChange={() => setPricesIncludeGst(false)}
                    className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-foreground block">
                      GST Exclusive (Added at checkout)
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-relaxed block mt-1">
                      Product price excludes tax. GST is added as an extra fee during checkout (e.g. ₹200 item + ₹36 GST + ₹60 shipping = <strong>₹296</strong> total).
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className="sm:w-64">
              <label className="eyebrow text-[11px] block mb-1.5">
                Default GST Tax Rate (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={gstRatePercent}
                  onChange={(e) =>
                    setGstRatePercent(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="min-h-10 w-full border border-border bg-background px-3 pr-8 text-xs font-semibold tabular-nums outline-none focus:border-primary rounded"
                  placeholder="18"
                />
                <Percent className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
              </div>
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Standard statutory GST rate for artificial botanicals (HSN 0602.90) is 18%.
              </span>
            </div>

            {/* Live Calculation Preview Banner */}
            <div className="rounded-lg bg-secondary/30 border border-border p-4 text-xs">
              <span className="font-bold text-foreground block mb-2">
                Live Pricing Example (Sample ₹200 Product):
              </span>
              {pricesIncludeGst ? (
                <div className="space-y-1 text-muted-foreground">
                  <div className="flex justify-between max-w-sm">
                    <span>Base Net Price:</span>
                    <strong className="text-foreground font-mono">
                      ₹{Math.round(200 / (1 + (Number(gstRatePercent) || 18) / 100))}
                    </strong>
                  </div>
                  <div className="flex justify-between max-w-sm">
                    <span>GST ({gstRatePercent || 18}% included):</span>
                    <strong className="text-foreground font-mono">
                      ₹{200 - Math.round(200 / (1 + (Number(gstRatePercent) || 18) / 100))}
                    </strong>
                  </div>
                  <div className="flex justify-between max-w-sm">
                    <span>Product Selling Price:</span>
                    <strong className="text-foreground font-mono">₹200</strong>
                  </div>
                  <div className="flex justify-between max-w-sm">
                    <span>Standard Shipping:</span>
                    <strong className="text-foreground font-mono">
                      ₹{standardShippingFee === "" ? 0 : standardShippingFee}
                    </strong>
                  </div>
                  <div className="flex justify-between max-w-sm border-t border-border/80 pt-1.5 font-bold text-foreground">
                    <span>Final Amount Charged:</span>
                    <span className="text-emerald-700 dark:text-emerald-400 font-mono text-sm">
                      ₹{200 + (standardShippingFee === "" ? 0 : Number(standardShippingFee))}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-muted-foreground">
                  <div className="flex justify-between max-w-sm">
                    <span>Product Selling Price:</span>
                    <strong className="text-foreground font-mono">₹200</strong>
                  </div>
                  <div className="flex justify-between max-w-sm">
                    <span>GST ({gstRatePercent || 18}% extra):</span>
                    <strong className="text-foreground font-mono">
                      ₹{Math.round(200 * ((Number(gstRatePercent) || 18) / 100))}
                    </strong>
                  </div>
                  <div className="flex justify-between max-w-sm">
                    <span>Standard Shipping:</span>
                    <strong className="text-foreground font-mono">
                      ₹{standardShippingFee === "" ? 0 : standardShippingFee}
                    </strong>
                  </div>
                  <div className="flex justify-between max-w-sm border-t border-border/80 pt-1.5 font-bold text-foreground">
                    <span>Final Amount Charged:</span>
                    <span className="text-amber-700 dark:text-amber-400 font-mono text-sm">
                      ₹{200 +
                        Math.round(200 * ((Number(gstRatePercent) || 18) / 100)) +
                        (standardShippingFee === "" ? 0 : Number(standardShippingFee))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex min-h-11 items-center gap-2 bg-primary px-6 text-xs uppercase tracking-wider font-semibold text-primary-foreground rounded hover:opacity-90 transition-opacity shadow-xs"
          >
            <Save className="h-4 w-4" /> Save Configuration
          </button>
        </div>
      </form>
    </div>
  );
}
